import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  ClipboardDocumentIcon,
  XCircleIcon,
  MinusCircleIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";

interface TestEntry {
  id: string;
  normalized_id: string;
  name: string;
  file: string;
  feature: string;
  feature_id: string;
  user_story: string;
  acceptance_criteria: string;
  requirement_ids: string[];
  priority: string;
  risk: string;
  risk_ids: string[];
  coverage_status: string;
  mapped: boolean;
  mapping_source: "qa_index" | "legacy_metadata" | "inferred";
  needs_review_reason: string[];
  type: string;
  framework: string;
  automation_status: string;
  last_run: string;
  result: string;
  duration_ms: number;
  error_message: string;
  flaky: boolean;
  outdated: boolean;
  outdated_reason: string;
  needs_review: boolean;
  recent_history: string[];
  notes: string;
}

interface FeatureCoverageEntry {
  total: number;
  tested: number;
  passed: number;
  failed: number;
}

interface QASummary {
  total_features: number;
  total_acceptance_criteria: number;
  total_risks: number;
  total_mapped_automated_tests: number;
  needs_review_count: number;
}

interface RequirementEntry {
  id: string;
  feature_id: string;
  feature_name: string;
  description: string;
  linked_risk_ids: string[];
  coverage_status: string;
  priority: string;
  mapped_test_ids: string[];
}

interface QAFeatureEntry {
  id: string;
  name: string;
  summary: string;
  requirements_count: number;
  risks_count: number;
  mapped_tests_count: number;
  needs_review_count: number;
  requirements_without_tests_count: number;
}

interface QARiskEntry {
  id: string;
  feature_id: string;
  feature_name: string;
  description: string;
  severity: string;
  likelihood: string;
  status: string;
  linked_test_ids: string[];
  residual_gap: string;
}

interface GapItem {
  id: string;
  kind: string;
  feature_id: string;
  feature_name: string;
  requirement_id: string;
  risk_id: string;
  description: string;
  priority: string;
}

interface NeedsReviewItem extends GapItem {
  test_id?: string;
}

interface DashboardData {
  generated_at: string;
  execution_evidence: {
    latest_result_at: string;
    matched_results_total: number;
    results_available: boolean;
    frameworks: Record<
      string,
      {
        result_file: string;
        result_file_exists: boolean;
        last_result_at: string;
        matched_results: number;
        discovered_tests: number;
      }
    >;
  };
  stale_state: {
    is_stale: boolean;
    generated_at: string;
    changed_groups: string[];
    newest_changed_path: string;
    newest_changed_at: string;
    groups: Record<
      string,
      {
        latest_path: string;
        latest_modified_at: string;
        changed_after_generation: { path: string; modified_at: string }[];
        missing_paths: string[];
        count: number;
      }
    >;
  };
  summary: {
    total: number;
    passed: number;
    failed: number;
    skipped: number;
    blocked: number;
    not_run: number;
    flaky: number;
    outdated: number;
    needs_review: number;
    total_duration_ms: number;
  };
  qa_summary: QASummary;
  qa_features: QAFeatureEntry[];
  qa_requirements: RequirementEntry[];
  qa_risks: QARiskEntry[];
  feature_coverage: Record<string, FeatureCoverageEntry>;
  risk_coverage: Record<string, FeatureCoverageEntry>;
  type_distribution: Record<string, number>;
  tests: TestEntry[];
  mapped_tests: string[];
  unmapped_tests: string[];
  requirements_without_tests: RequirementEntry[];
  high_risk_gaps: GapItem[];
  needs_review_items: NeedsReviewItem[];
  validation_errors: string[];
  broken_mappings: string[];
  duplicates: { test_a: string; test_b: string; similarity: number }[];
  needs_review: string[];
  history?: { timestamp: string; total: number; passed: number; failed: number }[];
}

const PRIORITY_ORDER = ["P0", "P1", "P2", "P3"];
const STATUS_ORDER = ["passed", "failed", "skipped", "blocked", "not_run"];
const RESULT_COLORS: Record<string, string> = {
  passed: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-800",
  skipped: "bg-amber-100 text-amber-800",
  blocked: "bg-slate-200 text-slate-600",
  not_run: "bg-slate-100 text-slate-500",
};
const BAR_COLORS: Record<string, string> = {
  passed: "bg-emerald-500",
  failed: "bg-red-500",
  skipped: "bg-amber-500",
  blocked: "bg-slate-400",
  not_run: "bg-slate-200 opacity-60",
};

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatDate(iso: string): string {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-AT", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "\u2014";
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function copyForAgent(test: TestEntry): string {
  if (test.outdated) {
    return [
      `Test may be outdated: ${test.id}`,
      `Error: ${test.error_message}`,
      `Reason: ${test.outdated_reason}`,
      `File: ${test.file}`,
      `Feature: ${test.feature} | Priority: ${test.priority || "none"} | Risk: ${test.risk || "none"}`,
      `Action: update the test to match the current API, or remove it if the tested code was intentionally dropped.`,
    ].join("\n");
  }
  if (test.result === "failed") {
    return [
      `Fix failing test: ${test.id}`,
      `Error: ${test.error_message}`,
      `File: ${test.file}`,
      `Feature: ${test.feature} | Priority: ${test.priority || "none"} | Risk: ${test.risk || "none"}`,
    ].join("\n");
  }
  if (test.flaky) {
    return [
      `Investigate flaky test: ${test.id}`,
      `Recent runs: ${test.recent_history.join(", ") || "mixed results"}`,
      `File: ${test.file}`,
      `Feature: ${test.feature} | Priority: ${test.priority || "none"}`,
      `Possible cause: race condition, timing dependency, or external service dependency.`,
    ].join("\n");
  }
  if (test.needs_review) {
    return [
      `Link test in QA docs:`,
      `Test ID: ${test.id}`,
      `Action: add this test to docs/testing/traceability-matrix.md and link it to an acceptance criterion or risk.`,
    ].join("\n");
  }
  return `${test.id}\n${test.file}`;
}

function copyGapForAgent(gap: GapItem) {
  return [
    `Close QA gap: ${gap.id}`,
    `Feature: ${gap.feature_name}`,
    gap.requirement_id ? `Requirement: ${gap.requirement_id}` : `Risk: ${gap.risk_id}`,
    `Priority: ${gap.priority}`,
    `Description: ${gap.description}`,
    `Action: add or strengthen automated coverage and update docs/testing/traceability-matrix.md.`,
  ].join("\n");
}

function copyNeedsReviewForAgent(item: NeedsReviewItem) {
  return [
    `Clarify QA item: ${item.id}`,
    `Feature: ${item.feature_name}`,
    item.test_id ? `Test: ${item.test_id}` : undefined,
    item.requirement_id ? `Requirement: ${item.requirement_id}` : undefined,
    item.risk_id ? `Risk: ${item.risk_id}` : undefined,
    `Description: ${item.description}`,
    `Action: resolve the uncertainty in the QA docs or link the missing test mapping.`,
  ]
    .filter(Boolean)
    .join("\n");
}

export const Route = createFileRoute("/app/test_dashboard")({
  component: TestDashboardPage,
});

function TestDashboardPage() {
  const { translate, language } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filterFeature, setFilterFeature] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterRisk, setFilterRisk] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFlaky, setFilterFlaky] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [copiedId, setCopiedId] = useState("");
  const translateRef = useRef(translate);
  translateRef.current = translate;

  useEffect(() => {
    setLoading(true);
    fetch("/test-dashboard-data.json")
      .then((res) => {
        if (!res.ok) throw new Error("No data");
        return res.json();
      })
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch(() => {
        setError(translateRef.current("test_dashboard_no_data"));
        setLoading(false);
      });
  }, []);

  const features = useMemo(
    () => [...new Set((data?.tests ?? []).map((t) => t.feature))].sort(),
    [data],
  );
  const priorities = useMemo(
    () => [...new Set((data?.tests ?? []).map((t) => t.priority).filter(Boolean))].sort((a, b) => PRIORITY_ORDER.indexOf(a) - PRIORITY_ORDER.indexOf(b)),
    [data],
  );
  const risks = useMemo(
    () => [...new Set((data?.tests ?? []).map((t) => t.risk).filter(Boolean))].sort(),
    [data],
  );
  const types = useMemo(
    () => [...new Set((data?.tests ?? []).map((t) => t.type))].sort(),
    [data],
  );

  const filteredTests = useMemo(() => {
    if (!data) return [];
    let tests = [...data.tests];
    if (filterFeature) tests = tests.filter((t) => t.feature === filterFeature);
    if (filterPriority) tests = tests.filter((t) => t.priority === filterPriority);
    if (filterRisk) tests = tests.filter((t) => t.risk === filterRisk);
    if (filterType) tests = tests.filter((t) => t.type === filterType);
    if (filterStatus) tests = tests.filter((t) => t.result === filterStatus);
    if (filterFlaky === "yes") tests = tests.filter((t) => t.flaky);
    if (filterFlaky === "no") tests = tests.filter((t) => !t.flaky);
    if (search) {
      const q = search.toLowerCase();
      tests = tests.filter(
        (t) =>
          t.id.toLowerCase().includes(q) ||
          t.name.toLowerCase().includes(q) ||
          t.feature.toLowerCase().includes(q) ||
          t.file.toLowerCase().includes(q),
      );
    }
    if (sortField) {
      tests.sort((a, b) => {
        const va = String((a as unknown as Record<string, unknown>)[sortField] ?? "");
        const vb = String((b as unknown as Record<string, unknown>)[sortField] ?? "");
        const cmp = va.localeCompare(vb);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return tests;
  }, [data, filterFeature, filterPriority, filterRisk, filterType, filterStatus, filterFlaky, search, sortField, sortDir]);

  const mappedCount = data?.mapped_tests.length ?? 0;
  const unmappedCount = data?.unmapped_tests.length ?? 0;
  const failedTests = useMemo(() => data?.tests.filter((test) => test.result === "failed") ?? [], [data]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleCopy = (test: TestEntry) => {
    copyToClipboard(copyForAgent(test));
    setCopiedId(test.id);
    setTimeout(() => setCopiedId(""), 1500);
  };

  const handleCopyAll = () => {
    if (!data) return;
    const sections: string[] = [];
    const failed = data.tests.filter((t) => t.result === "failed");
    const flaky = data.tests.filter((t) => t.flaky);
    const outdated = data.tests.filter((t) => t.outdated);
    const needsRev = data.tests.filter((t) => t.needs_review);
    const highRiskGaps = data.high_risk_gaps;
    const reviewItems = data.needs_review_items.filter((item) => !item.test_id);

    if (failed.length) {
      sections.push(`=== Failed Tests (${failed.length}) ===`);
      failed.forEach((t) => sections.push(copyForAgent(t), ""));
    }
    if (flaky.length) {
      sections.push(`=== Flaky Tests (${flaky.length}) ===`);
      flaky.forEach((t) => sections.push(copyForAgent(t), ""));
    }
    if (outdated.length) {
      sections.push(`=== Outdated Tests (${outdated.length}) ===`);
      outdated.forEach((t) => sections.push(copyForAgent(t), ""));
    }
    if (needsRev.length) {
      sections.push(`=== Tests Needing Review (${needsRev.length}) ===`);
      needsRev.slice(0, 20).forEach((t) => sections.push(copyForAgent(t), ""));
      if (needsRev.length > 20) sections.push(`... and ${needsRev.length - 20} more`);
    }
    if (highRiskGaps.length) {
      sections.push(`=== High-Risk Gaps (${highRiskGaps.length}) ===`);
      highRiskGaps.slice(0, 20).forEach((gap) => sections.push(copyGapForAgent(gap), ""));
    }
    if (reviewItems.length) {
      sections.push(`=== QA Needs Review (${reviewItems.length}) ===`);
      reviewItems.slice(0, 20).forEach((item) => sections.push(copyNeedsReviewForAgent(item), ""));
    }

    if (!sections.length) {
      copyToClipboard("All tests are passing with full metadata. No issues to report.");
    } else {
      copyToClipboard(sections.join("\n"));
    }
    setCopiedId("__all__");
    setTimeout(() => setCopiedId(""), 1500);
  };

  const maxFeatureTotal = useMemo(
    () => Math.max(1, ...Object.values(data?.feature_coverage ?? {}).map((f) => f.total)),
    [data],
  );

  const maxRiskTotal = useMemo(
    () => Math.max(1, ...Object.values(data?.risk_coverage ?? {}).map((r) => r.total)),
    [data],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-base text-slate-500">{translate("topic_surprise_me_loading")}</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6">
        <div className="rounded-[26px] border border-slate-200 bg-white px-5 py-12 text-center">
          <p className="text-base text-slate-500">{error || translate("test_dashboard_no_data")}</p>
        </div>
      </div>
    );
  }

  const s = data.summary;
  const executed = s.passed + s.failed + s.skipped;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6">
      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-medium text-slate-800">
            {translate("test_dashboard_generated_at_label")}: {formatDate(data.generated_at)}
          </span>
          <span className="font-medium text-slate-700">
            {translate("test_dashboard_results_at_label")}:{" "}
            {data.execution_evidence.latest_result_at
              ? formatDate(data.execution_evidence.latest_result_at)
              : translate("test_dashboard_none")}
          </span>
          {data.stale_state.is_stale ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {translate("test_dashboard_stale_badge")}
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              {translate("test_dashboard_fresh_badge")}
            </span>
          )}
        </div>
        {data.stale_state.is_stale && (
          <div className="mt-2 space-y-1 text-xs text-slate-600">
            <p>{translate("test_dashboard_stale_warning")}</p>
            <p>
              {translate("test_dashboard_stale_latest_change")}{" "}
              <span className="font-medium text-slate-700">{data.stale_state.newest_changed_path}</span>{" "}
              ({formatDate(data.stale_state.newest_changed_at)})
            </p>
            <p>
              {translate("test_dashboard_stale_changed_groups")}{" "}
              {data.stale_state.changed_groups.join(", ")}
            </p>
          </div>
        )}
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-[34rem] text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="px-2 py-1.5 font-medium">{translate("test_dashboard_framework_col")}</th>
                <th className="px-2 py-1.5 font-medium">{translate("test_dashboard_last_results_col")}</th>
                <th className="px-2 py-1.5 font-medium">{translate("test_dashboard_matched_results_col")}</th>
                <th className="px-2 py-1.5 font-medium">{translate("test_dashboard_result_file_col")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.execution_evidence.frameworks).map(([framework, info]) => (
                <tr key={framework} className="border-b border-slate-100 last:border-b-0 text-slate-700">
                  <td className="px-2 py-1.5 uppercase">{framework}</td>
                  <td className="px-2 py-1.5">
                    {info.last_result_at ? formatDate(info.last_result_at) : translate("test_dashboard_none")}
                  </td>
                  <td className="px-2 py-1.5">
                    {info.matched_results}/{info.discovered_tests}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">{info.result_file}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stat row */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="text-slate-500">{translate("test_dashboard_executed")}</span>
        <span className="font-semibold tabular-nums text-slate-900">{executed}</span>
        <span className="text-slate-300">/</span>
        <span className="text-slate-500">{translate("test_dashboard_passed")}</span>
        <span className="font-semibold tabular-nums text-emerald-700">{s.passed}</span>
        <span className="text-slate-300">/</span>
        <span className="text-slate-500">{translate("test_dashboard_failed")}</span>
        <span className="font-semibold tabular-nums text-red-600">{s.failed}</span>
        <span className="text-slate-300">/</span>
        <span className="text-slate-500">{translate("test_dashboard_skipped")}</span>
        <span className="tabular-nums text-amber-600">{s.skipped}</span>
        <span className="text-slate-300">/</span>
        <span className="text-slate-500">{translate("test_dashboard_flaky")}</span>
        <span className="tabular-nums text-purple-600">{s.flaky}</span>
        <span className="text-xs text-slate-400 ml-auto">
          {translate("test_dashboard_last_run")}: {formatDate(data.generated_at)}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-5">
        <SummaryPill
          label={translate("test_dashboard_summary_features")}
          value={data.qa_summary.total_features}
        />
        <SummaryPill
          label={translate("test_dashboard_summary_requirements")}
          value={data.qa_summary.total_acceptance_criteria}
        />
        <SummaryPill
          label={translate("test_dashboard_summary_risks")}
          value={data.qa_summary.total_risks}
        />
        <SummaryPill
          label={translate("test_dashboard_summary_mapped")}
          value={data.qa_summary.total_mapped_automated_tests}
        />
        <SummaryPill
          label={translate("test_dashboard_summary_unmapped")}
          value={unmappedCount}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TableCard
          title={`${translate("test_dashboard_failed")} ${s.failed}`}
          hint={translate("test_dashboard_failed_tests_hint")}
        >
          <SimpleTable
            headers={["Test", "Feature", "Status"]}
            maxHeightClass="max-h-none"
            rows={
              failedTests.length > 0
                ? failedTests.map((test) => [
                    <span title={test.id} className="block whitespace-normal break-all">{test.id}</span>,
                    <span className="whitespace-normal break-words">{test.feature}</span>,
                    <StatusBadge status="failed" label="failed" />,
                  ])
                : [[translate("test_dashboard_none"), "", ""]]
            }
          />
        </TableCard>

        <TableCard
          title={translate("test_dashboard_mapping_overview")}
          hint={translate("test_dashboard_mapping_overview_hint")}
        >
          <SimpleTable
            headers={["Type", "Count", "Meaning"]}
            maxHeightClass="max-h-none"
            rows={[
              [
                <span className="whitespace-normal break-words">{translate("test_dashboard_summary_mapped")}</span>,
                String(mappedCount),
                <span className="block whitespace-normal break-words">{translate("test_dashboard_mapped_tests_detail_hint")}</span>,
              ],
              [
                <span className="whitespace-normal break-words">{translate("test_dashboard_summary_unmapped")}</span>,
                String(unmappedCount),
                <span className="block whitespace-normal break-words">{translate("test_dashboard_unmapped_tests_detail_hint")}</span>,
              ],
            ]}
          />
        </TableCard>
      </div>

      <div className="mt-4">
        <TableCard
          title={`${translate("test_dashboard_summary_features")} ${data.qa_summary.total_features}`}
          hint={translate("test_dashboard_documented_features_hint")}
        >
          <SimpleTable
            headers={["Feature", "AC", "Risks", "Mapped tests", "Open AC"]}
            maxHeightClass="max-h-[32rem]"
            minWidthClass="min-w-[48rem]"
            rows={data.qa_features.map((feature) => [
              feature.name,
              String(feature.requirements_count),
              String(feature.risks_count),
              String(feature.mapped_tests_count),
              String(feature.requirements_without_tests_count),
            ])}
          />
        </TableCard>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TableCard
          title={`${translate("test_dashboard_summary_requirements")} ${data.qa_summary.total_acceptance_criteria}`}
          hint={translate("test_dashboard_acceptance_criteria_hint")}
        >
          <SimpleTable
            headers={["ID", "Feature", "Priority", "Mapped tests"]}
            maxHeightClass="max-h-[36rem]"
            minWidthClass="min-w-[40rem]"
            rows={data.qa_requirements.map((requirement) => [
              requirement.id,
              requirement.feature_name,
              requirement.priority || "—",
              String(requirement.mapped_test_ids.length),
            ])}
          />
        </TableCard>

        <TableCard
          title={`${translate("test_dashboard_summary_risks")} ${data.qa_summary.total_risks}`}
          hint={translate("test_dashboard_documented_risks_hint")}
        >
          <SimpleTable
            headers={["ID", "Feature", "Severity", "Linked tests"]}
            maxHeightClass="max-h-[36rem]"
            minWidthClass="min-w-[40rem]"
            rows={data.qa_risks.map((risk) => [
              risk.id,
              risk.feature_name,
              risk.severity,
              String(risk.linked_test_ids.length),
            ])}
          />
        </TableCard>
      </div>

      {/* Copy all issues */}
      {(s.failed > 0 || s.flaky > 0 || s.outdated > 0 || s.needs_review > 0) && (
        <div className="mt-2 flex justify-end">
          <button
            onClick={handleCopyAll}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            <ClipboardDocumentIcon className="w-3.5 h-3.5" />
            {copiedId === "__all__" ? translate("tooltip_copied") : "Copy all issues for agent"}
          </button>
        </div>
      )}

      {/* Charts Row — no card wrappers */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Status Breakdown */}
        <ChartSection title={translate("test_dashboard_status_breakdown")}>
          {(() => {
            const statusCounts = { passed: s.passed, failed: s.failed, skipped: s.skipped, not_run: s.not_run };
            const total = s.total || 1;
            return (["passed", "failed", "skipped", "not_run"] as const).map((status) => {
              const count = statusCounts[status];
              const pct = (count / total) * 100;
              const isNotRun = status === "not_run";
              return (
                <div key={status} className="flex items-center gap-2">
                  <span className={`w-16 text-xs capitalize ${isNotRun ? "text-slate-400" : "text-slate-600"}`}>
                    {status.replace("_", " ")}
                  </span>
                  <div className="flex-1 h-5 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${BAR_COLORS[status]}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`w-24 text-right text-xs tabular-nums ${isNotRun ? "text-slate-400" : "font-medium text-slate-700"}`}>
                    {count} <span className="text-slate-400 font-normal">({count === 0 ? "0.0" : pct.toFixed(1)}%)</span>
                  </span>
                </div>
              );
            });
          })()}
        </ChartSection>

        {/* Feature Coverage */}
        <ChartSection title={translate("test_dashboard_feature_coverage")}>
          <div className="max-h-52 overflow-y-auto space-y-2">
            {Object.entries(data.feature_coverage).map(([feature, fc]) => {
              const pct = (fc.tested / Math.max(fc.total, maxFeatureTotal)) * 100;
              return (
                <div key={feature} className="flex items-center gap-2">
                  <span className="w-28 text-xs text-slate-600 truncate" title={feature}>{feature}</span>
                  <div className="flex-1 h-4 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-14 text-right text-xs font-medium text-slate-700">{fc.tested}/{fc.total}</span>
                </div>
              );
            })}
            {Object.keys(data.feature_coverage).length === 0 && (
              <p className="text-xs text-slate-400 py-2">{translate("test_dashboard_feature_coverage_empty")}</p>
            )}
          </div>
        </ChartSection>

        {/* Risk Coverage */}
        <ChartSection title={translate("test_dashboard_risk_coverage")}>
          <p className="pb-2 text-xs text-slate-500">{translate("test_dashboard_risk_coverage_hint")}</p>
          {Object.entries(data.risk_coverage).map(([risk, rc]) => {
            const pct = (rc.tested / Math.max(rc.total, maxRiskTotal)) * 100;
            return (
              <div key={risk} className="flex items-center gap-2">
                <span className="w-16 text-xs text-slate-600">{risk}</span>
                <div className="flex-1 h-5 bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${risk === "High" ? "bg-red-500" : risk === "Medium" ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-14 text-right text-xs font-medium text-slate-700">{rc.tested}/{rc.total}</span>
              </div>
            );
          })}
          {Object.keys(data.risk_coverage).length === 0 && (
            <p className="text-xs text-slate-400 py-2">{translate("test_dashboard_risk_coverage_empty")}</p>
          )}
        </ChartSection>

        {/* Type Distribution */}
        <ChartSection title={translate("test_dashboard_type_distribution")}>
          {Object.entries(data.type_distribution).map(([type, count]) => {
            const maxType = Math.max(1, ...Object.values(data.type_distribution));
            const pct = (count / maxType) * 100;
            return (
              <div key={type} className="flex items-center gap-2">
                <span className="w-10 text-xs text-slate-600 uppercase">{type}</span>
                <div className="flex-1 h-5 bg-slate-100 overflow-hidden">
                  <div className="h-full bg-indigo-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right text-xs font-medium text-slate-700">{count}</span>
              </div>
            );
          })}
        </ChartSection>

        {/* Pass Rate Trend */}
        {data.history && data.history.length >= 2 && (
          <ChartSection title={translate("test_dashboard_pass_rate_trend")}>
            <div className="flex items-end gap-1 h-20">
              {data.history.slice(-20).map((h, i) => {
                const rate = h.total ? (h.passed / h.total) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[9px] text-slate-500">{Math.round(rate)}%</span>
                    <div className="w-full bg-emerald-400" style={{ height: `${rate * 0.7}%` }} />
                  </div>
                );
              })}
            </div>
          </ChartSection>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartSection title={translate("test_dashboard_high_risk_gaps_section")}>
          <div className="space-y-2">
            <p className="text-xs text-slate-600">{translate("test_dashboard_high_risk_gaps_hint")}</p>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <tbody>
                  {data.high_risk_gaps.length === 0 && (
                    <tr>
                      <td className="px-2 py-3 text-slate-400">{translate("test_dashboard_none")}</td>
                    </tr>
                  )}
                  {data.high_risk_gaps.map((gap) => (
                    <tr key={gap.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-1">
                        <p className="font-medium text-slate-700">
                          {gap.requirement_id || gap.risk_id}
                        </p>
                        <p className="text-slate-500">{gap.feature_name}</p>
                        <p className="mt-0.5 text-slate-500">{gap.description}</p>
                      </td>
                      <td className="px-2 py-1 text-right align-top">
                        <button
                          onClick={() => {
                            copyToClipboard(copyGapForAgent(gap));
                            setCopiedId(gap.id);
                            setTimeout(() => setCopiedId(""), 1500);
                          }}
                          className="rounded p-1 hover:bg-slate-100"
                          title={language === "GERMAN" ? "Für Agent kopieren" : "Copy for agent"}
                        >
                          <ClipboardDocumentIcon className={`h-3 w-3 ${copiedId === gap.id ? "text-emerald-600" : "text-slate-400"}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ChartSection>

        <ChartSection title={translate("test_dashboard_requirements_without_tests_section")}>
          <div className="space-y-2">
            <p className="text-xs text-slate-600">{translate("test_dashboard_requirements_without_tests_hint")}</p>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <tbody>
                  {data.requirements_without_tests.length === 0 && (
                    <tr>
                      <td className="px-2 py-3 text-slate-400">{translate("test_dashboard_none")}</td>
                    </tr>
                  )}
                  {data.requirements_without_tests.map((requirement) => (
                    <tr key={requirement.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-1">
                        <p className="font-medium text-slate-700">{requirement.id}</p>
                        <p className="text-slate-500">{requirement.feature_name}</p>
                        <p className="mt-0.5 text-slate-500">{requirement.description}</p>
                      </td>
                      <td className="px-2 py-1 text-slate-500 align-top whitespace-nowrap">
                        {requirement.priority || "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ChartSection>
      </div>

      {/* Filters — no card */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <FunnelIcon className="w-4 h-4 text-slate-400" />
          <select
            value={filterFeature}
            onChange={(e) => setFilterFeature(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_feature")}</option>
            {features.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_priority")}</option>
            {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_risk")}</option>
            {risks.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_type")}</option>
            {types.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_status")}</option>
            {STATUS_ORDER.map((st) => <option key={st} value={st}>{st.replace("_", " ")}</option>)}
          </select>
          <select
            value={filterFlaky}
            onChange={(e) => setFilterFlaky(e.target.value)}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 bg-white"
          >
            <option value="">{translate("test_dashboard_filter_flaky")}</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={translate("test_dashboard_filter_search")}
              className="w-32 bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none"
            />
          </div>
      </div>

      {/* Test Table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              <Th label={translate("test_dashboard_col_name")} field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_feature")} field="feature" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_mapping")} field="mapping_source" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_priority")} field="priority" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_risk")} field="risk" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_requirements")} field="acceptance_criteria" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_type")} field="type" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_status")} field="result" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <Th label={translate("test_dashboard_col_duration")} field="duration_ms" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <th className="px-2 py-2 font-medium text-slate-500">#</th>
            </tr>
          </thead>
          <tbody>
            {filteredTests.slice(0, 200).map((test) => (
              <tr key={test.id} className="border-b border-slate-100 hover:bg-slate-50 group">
                <td className="px-3 py-2 max-w-xs">
                  <div className="flex items-center gap-1.5">
                    {test.flaky && <BoltIcon className="w-3 h-3 text-purple-500 flex-shrink-0" title={translate("test_dashboard_flaky")} />}
                    {test.outdated && <ExclamationTriangleIcon className="w-3 h-3 text-orange-500 flex-shrink-0" title={test.outdated_reason} />}
                    {test.result === "failed" && <XCircleIcon className="w-3 h-3 text-red-500 flex-shrink-0" />}
                    <span className="truncate text-slate-700" title={test.id}>{test.name}</span>
                  </div>
                  {test.error_message && test.result === "failed" && (
                    <p className="mt-0.5 text-[10px] text-red-600 truncate max-w-xs" title={test.error_message}>
                      {test.error_message.split("\n")[0].slice(0, 120)}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{test.feature}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      test.mapped
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {test.mapped
                      ? translate("test_dashboard_mapping_mapped")
                      : translate("test_dashboard_mapping_unmapped")}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {test.priority ? (
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${test.priority === "P0" ? "bg-red-100 text-red-700" : test.priority === "P1" ? "bg-orange-100 text-orange-700" : test.priority === "P2" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {test.priority}
                    </span>
                  ) : (
                    <span className="text-slate-300">\u2014</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {test.risk ? (
                    <span className="text-slate-600">{test.risk}</span>
                  ) : (
                    <span className="text-slate-300">\u2014</span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                  {test.requirement_ids.length > 0
                    ? `${test.requirement_ids.length}`
                    : "\u2014"}
                </td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap uppercase">{test.type}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${RESULT_COLORS[test.result] || "bg-slate-100 text-slate-600"}`}>
                    {test.result}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                  {test.duration_ms > 0 ? formatMs(test.duration_ms) : "\u2014"}
                </td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => handleCopy(test)}
                    className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-slate-100"
                    title={language === "GERMAN" ? "Für Agent kopieren" : "Copy for agent"}
                  >
                    <ClipboardDocumentIcon className={`w-3.5 h-3.5 ${copiedId === test.id ? "text-emerald-600" : "text-slate-400"}`} />
                  </button>
                </td>
              </tr>
            ))}
            {filteredTests.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  {translate("test_dashboard_no_matching_tests")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredTests.length > 200 && (
          <p className="px-4 py-2 text-xs text-slate-400">
            Showing 200 of {filteredTests.length} filtered tests. Narrow your filters to see more.
          </p>
        )}
      </div>

      {/* Needs Review */}
      {data.needs_review_items.length > 0 && (
        <div className="mt-6 bg-amber-50/50 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            <h2 className="font-serif text-lg text-slate-900">
              {translate("test_dashboard_needs_review_section")}
            </h2>
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800">
              {data.needs_review_items.length}
            </span>
          </div>
          <p className="mb-3 text-xs text-slate-600">
            {translate("test_dashboard_needs_review_hint")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-amber-200 text-left">
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_col_id")}</th>
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_col_name")}</th>
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_col_feature")}</th>
                  <th className="px-2 py-1.5 font-medium text-slate-500">#</th>
                </tr>
              </thead>
              <tbody>
                {data.needs_review_items.slice(0, 50).map((item) => (
                  <tr key={item.id} className="border-b border-amber-100 hover:bg-amber-100/50 group">
                    <td className="px-2 py-1 text-slate-500 font-mono text-[10px] max-w-40 truncate" title={item.id}>{item.id}</td>
                    <td className="px-2 py-1 text-slate-700 max-w-48 truncate" title={item.description}>
                      {item.test_id || item.description}
                    </td>
                    <td className="px-2 py-1 text-slate-600 whitespace-nowrap">{item.feature_name}</td>
                    <td className="px-2 py-1">
                      <button
                        onClick={() => {
                          copyToClipboard(
                            item.test_id
                              ? copyNeedsReviewForAgent(item)
                              : copyNeedsReviewForAgent(item),
                          );
                          setCopiedId(item.id);
                          setTimeout(() => setCopiedId(""), 1500);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-amber-200"
                        title={language === "GERMAN" ? "Für Agent kopieren" : "Copy for agent"}
                      >
                        <ClipboardDocumentIcon className={`w-3 h-3 ${copiedId === item.id ? "text-emerald-600" : "text-slate-400"}`} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.needs_review_items.length > 50 && (
            <p className="mt-2 text-xs text-slate-400">
              {translate("test_dashboard_needs_review_more_prefix")} 50 {translate("test_dashboard_needs_review_more_middle")} {data.needs_review_items.length}.
            </p>
          )}
        </div>
      )}

      {/* Duplicates */}
      {data.duplicates.length > 0 && (
        <div className="mt-6 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-2 mb-3">
            <MinusCircleIcon className="w-5 h-5 text-slate-500" />
            <h2 className="font-serif text-lg text-slate-900">
              {translate("test_dashboard_duplicates_section")}
            </h2>
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
              {data.duplicates.length}
            </span>
          </div>
          <p className="mb-3 text-xs text-slate-600">
            {translate("test_dashboard_duplicates_hint")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_col_name")}</th>
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_col_name")}</th>
                  <th className="px-2 py-1.5 font-medium text-slate-500">{translate("test_dashboard_similarity")}</th>
                </tr>
              </thead>
              <tbody>
                {data.duplicates.map((dup, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-2 py-1 text-slate-600 max-w-56 truncate" title={dup.test_a}>{dup.test_a}</td>
                    <td className="px-2 py-1 text-slate-600 max-w-56 truncate" title={dup.test_b}>{dup.test_b}</td>
                    <td className="px-2 py-1">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                        {Math.round(dup.similarity * 100)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.broken_mappings.length > 0 && (
        <div className="mt-6 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-2 mb-3">
            <XCircleIcon className="w-5 h-5 text-red-500" />
            <h2 className="font-serif text-lg text-slate-900">
              {translate("test_dashboard_broken_mappings_section")}
            </h2>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {data.broken_mappings.join("\n")}
          </div>
        </div>
      )}
    </div>
  );
}

function ChartSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function SummaryPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

function TableCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-4 py-4">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function SimpleTable({
  headers,
  rows,
  maxHeightClass = "max-h-72",
  minWidthClass = "min-w-full",
}: {
  headers: string[];
  rows: React.ReactNode[][];
  maxHeightClass?: string;
  minWidthClass?: string;
}) {
  return (
    <div className={`overflow-x-auto overflow-y-auto rounded-lg border border-slate-200 ${maxHeightClass}`}>
      <table className={`${minWidthClass} text-xs`}>
        <thead className="sticky top-0 bg-slate-50">
          <tr className="border-b border-slate-200 text-left">
            {headers.map((header) => (
              <th key={header} className="px-3 py-2 font-medium text-slate-500 whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-slate-100 last:border-b-0">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top text-slate-700 break-words">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${RESULT_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

function Th({
  label,
  field,
  sortField,
  sortDir,
  onSort,
}: {
  label: string;
  field: string;
  sortField: string;
  sortDir: "asc" | "desc";
  onSort: (f: string) => void;
}) {
  return (
    <th
      className="px-3 py-2 font-medium text-slate-500 cursor-pointer hover:text-slate-700 select-none whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          <span className="text-[10px]">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>
        )}
      </span>
    </th>
  );
}
